import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  DEFAULT_TEMPLATES,
  errorMessage,
  renderTemplate,
  saveCoverTemplates,
  TEMPLATE_PLACEHOLDERS,
} from "../../utils/coversApi";

const SAMPLE_VARS = Object.fromEntries(TEMPLATE_PLACEHOLDERS.map((p) => [p.key, p.sample]));

/**
 * Lets managers maintain the list of cover message templates. One is picked
 * at random for each new request; placeholders are filled per tutor.
 */
const CoverTemplatesDialog = ({ open, onClose, templates, hasSaved }) => {
  const [drafts, setDrafts] = useState([]);
  const [focused, setFocused] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    if (open) {
      setDrafts(templates.length ? [...templates] : [...DEFAULT_TEMPLATES]);
      setFocused(0);
      setError("");
    }
  }, [open, templates]);

  const update = (i, value) => setDrafts((d) => d.map((t, j) => (j === i ? value : t)));
  const remove = (i) => setDrafts((d) => d.filter((_, j) => j !== i));
  const add = () => {
    setDrafts((d) => [...d, ""]);
    setFocused(drafts.length);
  };

  const insertPlaceholder = (key) => {
    const i = Math.min(focused, drafts.length - 1);
    if (i < 0) return;
    const el = inputRefs.current[i];
    const token = `{${key}}`;
    const text = drafts[i] || "";
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + token + text.slice(end);
    update(i, next);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      }
    });
  };

  const handleSave = async () => {
    const clean = drafts.map((t) => t.trim()).filter(Boolean);
    if (clean.length === 0) {
      setError("Add at least one template.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await saveCoverTemplates({ templates: clean });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const previewIndex = Math.min(focused, drafts.length - 1);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Cover message templates</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            One template is chosen at random for each new cover request. Placeholders are filled in
            for each tutor, so every tutor gets a personal message. Click a placeholder to insert it
            into the selected template.
          </Typography>

          {!hasSaved && (
            <Alert severity="info">
              No templates saved yet. These are the built-in defaults; edit them and save to make
              them yours.
            </Alert>
          )}

          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {TEMPLATE_PLACEHOLDERS.map((p) => (
              <Tooltip key={p.key} title={`Example: ${p.sample}`}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`{${p.key}} · ${p.label}`}
                  onClick={() => insertPlaceholder(p.key)}
                />
              </Tooltip>
            ))}
          </Stack>

          <Stack gap={1.5}>
            {drafts.map((t, i) => (
              <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label={`Template ${i + 1}`}
                  value={t}
                  inputRef={(el) => (inputRefs.current[i] = el)}
                  onFocus={() => setFocused(i)}
                  onChange={(e) => update(i, e.target.value)}
                  inputProps={{ "data-testid": `template-${i}` }}
                />
                <Tooltip title="Remove template">
                  <span>
                    <IconButton
                      aria-label={`Remove template ${i + 1}`}
                      onClick={() => remove(i)}
                      disabled={drafts.length <= 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>

          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={drafts.length >= 20}>
              Add template
            </Button>
          </Box>

          {previewIndex >= 0 && drafts[previewIndex] && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
              <Typography variant="caption" color="text.secondary">
                Preview of template {previewIndex + 1} as Kyle would see it
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {renderTemplate(drafts[previewIndex], SAMPLE_VARS)}
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save templates"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverTemplatesDialog;
